import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  Download,
  Eye,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  Users,
  X
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../utils/auth';
import { CLIENT_MANAGEMENT_PRIVILEGES } from '../../../utils/clientManagementPrivileges';
import api from '../../../utils/api';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './CorporateClientData.css';
import { API_BASE_URL } from '../../../utils/apiBase';
import { confirmDialog } from '../../../components/notifications/notificationService';

const CorporateClientData = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('edit');
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [confirmationCompany, setConfirmationCompany] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    owner: '',
    assignedUser: '',
    city: '',
    state: '',
    status: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [quickViewClient, setQuickViewClient] = useState(null);
  const initialClientState = useRef(null);
  const privileges = getUserPrivileges();
  const role = getUserRole();
  const corporatePrivileges = CLIENT_MANAGEMENT_PRIVILEGES.corporate;
  const hasCorporatePrivilege = (key) => role === 'admin' || privileges.includes(key);

  const canExportCorporateClients = hasCorporatePrivilege(corporatePrivileges.export);
  const canAddCorporateClients = hasCorporatePrivilege(corporatePrivileges.add);
  const canViewCorporateClients = hasCorporatePrivilege(corporatePrivileges.view);
  const canDeleteCorporateClients =
    hasCorporatePrivilege(corporatePrivileges.delete);
  const canEditCorporateClients =
    hasCorporatePrivilege(corporatePrivileges.edit);
  const canListCorporateClients =
    canViewCorporateClients || canExportCorporateClients || canEditCorporateClients || canDeleteCorporateClients;
  const canShowCorporateActions = canViewCorporateClients || canEditCorporateClients || canDeleteCorporateClients;
  const canReassignCorporateOwner = role === 'admin';

  const formatAuditTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-IN');
  };

  const getOwnerValue = (owner) => {
    if (!owner) return '';
    return typeof owner === 'object' ? owner._id || '' : owner;
  };

  const getOwnerLabel = useCallback(
    (client) => client?.ownerId?.name || client?.ownerId?.username || client?.ownerName || '-',
    []
  );

  const getAssignedUserLabel = useCallback(
    (client) =>
      client?.assignedUser?.name ||
      client?.assignedUser?.username ||
      client?.assignedTo?.name ||
      client?.assignedTo?.username ||
      client?.assignedUserName ||
      getOwnerLabel(client),
    [getOwnerLabel]
  );

  const getClientStatus = (client) => {
    const explicitStatus = client?.status || client?.clientStatus || client?.activeStatus;
    if (explicitStatus) return String(explicitStatus);
    return client?.isProspect === false ? 'Active' : 'Prospect';
  };

  const getInitials = (name = '') => {
    const words = String(name).trim().split(/\s+/).filter(Boolean);
    if (!words.length) return 'CC';
    return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  };

  const getClientReference = (client) => client?.autoGeneratedId || '-';

  const isAddedThisMonth = (client) => {
    const createdDate = new Date(client?.createdAt || client?.updatedAt);
    if (Number.isNaN(createdDate.getTime())) return false;
    const now = new Date();
    return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
  };

  const hasCompliance = (client) => Boolean(client?.gst || client?.pan);

  const uniqueOptions = (values) =>
    [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    );

  useEffect(() => {
    if (!canListCorporateClients) {
      setLoading(false);
      return;
    }

    const fetchClients = async () => {
      try {
        const response = await api.get(`${API_BASE_URL}/api/corporate-clients/list`);
        setClients(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching corporate clients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [canListCorporateClients]);

  useEffect(() => {
    if (!canReassignCorporateOwner) return;

    const fetchEmployees = async () => {
      try {
        const response = await api.get(`${API_BASE_URL}/api/auth/employee-options`);
        setEmployeeOptions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching employee options:', error);
      }
    };

    fetchEmployees();
  }, [canReassignCorporateOwner]);

  const summary = useMemo(() => {
    const activeClients = clients.filter((client) => getClientStatus(client).toLowerCase() === 'active').length;
    return {
      total: clients.length,
      active: activeClients,
      addedThisMonth: clients.filter(isAddedThisMonth).length,
      assigned: clients.filter((client) => getOwnerLabel(client) !== '-' || getAssignedUserLabel(client) !== '-').length,
      complianceReady: clients.filter(hasCompliance).length
    };
  }, [clients, getAssignedUserLabel, getOwnerLabel]);

  const filterOptions = useMemo(
    () => ({
      owners: uniqueOptions(clients.map(getOwnerLabel).filter((value) => value !== '-')),
      assignedUsers: uniqueOptions(clients.map(getAssignedUserLabel).filter((value) => value !== '-')),
      cities: uniqueOptions(clients.map((client) => client.city)),
      states: uniqueOptions(clients.map((client) => client.state)),
      statuses: uniqueOptions(clients.map(getClientStatus))
    }),
    [clients, getAssignedUserLabel, getOwnerLabel]
  );

  const filteredClients = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    return clients.filter((client) => {
      const searchText = [
        client.autoGeneratedId,
        client.companyName,
        client.contactPersonName,
        client.contactNo,
        client.email,
        client.gst,
        client.pan,
        client._id
      ].join(' ').toLowerCase();

      const matchesSearch = !query || searchText.includes(query);
      const matchesOwner = !appliedFilters.owner || getOwnerLabel(client) === appliedFilters.owner;
      const matchesAssigned =
        !appliedFilters.assignedUser || getAssignedUserLabel(client) === appliedFilters.assignedUser;
      const matchesCity = !appliedFilters.city || client.city === appliedFilters.city;
      const matchesState = !appliedFilters.state || client.state === appliedFilters.state;
      const matchesStatus = !appliedFilters.status || getClientStatus(client) === appliedFilters.status;

      return matchesSearch && matchesOwner && matchesAssigned && matchesCity && matchesState && matchesStatus;
    });
  }, [clients, appliedFilters, getAssignedUserLabel, getOwnerLabel]);

  const selectedClients = useMemo(
    () => clients.filter((client) => selectedClientIds.includes(client._id)),
    [clients, selectedClientIds]
  );

  const summaryCards = [
    { label: 'Total Corporate Clients', value: summary.total, note: 'All company records', icon: Building2, tone: 'is-blue' },
    { label: 'Active Clients', value: summary.active, note: 'Converted clients', icon: ShieldCheck, tone: 'is-green' },
    { label: 'Added This Month', value: summary.addedThisMonth, note: 'New companies', icon: UserCheck, tone: 'is-amber' },
    { label: 'Assigned Clients', value: summary.assigned, note: 'Owner mapped', icon: BriefcaseBusiness, tone: 'is-teal' },
    { label: 'GST/PAN Ready', value: summary.complianceReady, note: 'Compliance details', icon: BadgeCheck, tone: 'is-indigo' }
  ];

  const openClientModal = (client, mode = 'edit') => {
    const normalizedClient = {
      ...client,
      ownerHistory: client.ownerHistory || []
    };
    setSelectedClient(normalizedClient);
    initialClientState.current = JSON.parse(JSON.stringify(normalizedClient));
    setModalMode(mode);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedClient(null);
    initialClientState.current = null;
    setModalMode('edit');
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setSelectedClientIds([]);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      search: '',
      owner: '',
      assignedUser: '',
      city: '',
      state: '',
      status: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSelectedClientIds([]);
  };

  const exportClients = (rows = filteredClients, filename = 'corporate-clients.xlsx') => {
    const exportRows = rows.map((client) => ({
      'Client ID': client.autoGeneratedId || '',
      'Company Name': client.companyName || '',
      'Contact Person': client.contactPersonName || '',
      'Contact No': client.contactNo || '',
      Email: client.email || '',
      Owner: getOwnerLabel(client),
      'Assigned User': getAssignedUserLabel(client),
      City: client.city || '',
      State: client.state || '',
      Status: getClientStatus(client),
      GST: client.gst || '',
      PAN: client.pan || '',
      Income: client.income || '',
      Address: client.address || '',
      Notes: client.note || ''
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Corporate Clients');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), filename);
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const toggleAllFilteredClients = (checked) => {
    setSelectedClientIds(checked ? filteredClients.map((client) => client._id) : []);
  };

  const buildUpdatePayload = (client) => ({
    customerType: client.customerType || 'Corporate',
    contactPersonName: client.contactPersonName,
    contactNo: client.contactNo,
    companyName: client.companyName,
    email: client.email,
    pinCode: client.pinCode,
    state: client.state,
    city: client.city,
    address: client.address,
    income: client.income,
    gst: client.gst,
    pan: client.pan,
    note: client.note,
    isProspect: client.isProspect,
    ownerId: getOwnerValue(client.ownerId)
  });

  const handleBulkDelete = async () => {
    if (!canDeleteCorporateClients || selectedClientIds.length === 0) return;
    const confirmed = await confirmDialog({ title: 'Delete selected clients?', message: `${selectedClientIds.length} corporate client record(s) will be permanently removed.`, confirmText: 'Delete Clients' });
    if (!confirmed) return;

    try {
      await Promise.all(
        selectedClientIds.map((clientId) => api.delete(`${API_BASE_URL}/api/corporate-clients/delete/${clientId}`))
      );
      setClients((prev) => prev.filter((client) => !selectedClientIds.includes(client._id)));
      setSelectedClientIds([]);
      alert('Selected corporate clients deleted successfully.');
    } catch (error) {
      console.error('Error deleting selected corporate clients:', error);
      alert('Unable to delete all selected corporate clients.');
    }
  };

  const handleBulkAssignOwner = async () => {
    if (!canReassignCorporateOwner || selectedClientIds.length === 0) return;
    const ownerId = window.prompt('Enter the owner user ID to assign selected clients:');
    if (!ownerId) return;

    try {
      const responses = await Promise.all(
        selectedClients.map((client) =>
          api.put(`${API_BASE_URL}/api/corporate-clients/update/${client._id}`, {
            ...buildUpdatePayload(client),
            ownerId
          })
        )
      );
      const updatedClients = responses.map((response) => response.data);
      setClients((prev) =>
        prev.map((client) => updatedClients.find((updatedClient) => updatedClient._id === client._id) || client)
      );
      setSelectedClientIds([]);
      alert('Selected corporate clients reassigned successfully.');
    } catch (error) {
      console.error('Error assigning selected corporate clients:', error);
      alert('Unable to assign selected corporate clients.');
    }
  };

  const handleOpenDeleteConfirmationModal = (client) => {
    if (!canDeleteCorporateClients) {
      alert('You do not have permission to delete corporate clients.');
      return;
    }

    setSelectedClient(client);
    setConfirmationCompany('');
    setShowDeleteConfirmationModal(true);
  };

  const handleCloseDeleteConfirmationModal = () => {
    setShowDeleteConfirmationModal(false);
    setConfirmationCompany('');
  };

  const handleDelete = async () => {
    if (!canDeleteCorporateClients) {
      alert('You do not have permission to delete corporate clients.');
      setShowDeleteConfirmationModal(false);
      return;
    }

    const expectedValue = String(selectedClient?.companyName || '').trim();
    if (confirmationCompany.trim() !== expectedValue) {
      alert('Company Name does not match.');
      return;
    }

    try {
      await api.delete(`${API_BASE_URL}/api/corporate-clients/delete/${selectedClient._id}`);
      setClients((prev) => prev.filter((client) => client._id !== selectedClient._id));
      setShowDeleteConfirmationModal(false);
      handleCloseModal();
      alert('Corporate client deleted successfully.');
    } catch (error) {
      console.error('Error deleting corporate client:', error);
      alert('Error deleting corporate client.');
    }
  };

  const handleUpdate = async () => {
    if (!canEditCorporateClients) {
      alert('You do not have permission to edit corporate clients.');
      handleCloseModal();
      return;
    }

    if (!selectedClient?._id) return;

    const hasChanges = JSON.stringify(selectedClient) !== JSON.stringify(initialClientState.current);

    if (!hasChanges) {
      alert('No changes detected to save.');
      handleCloseModal();
      return;
    }

    try {
      const response = await api.put(
        `${API_BASE_URL}/api/corporate-clients/update/${selectedClient._id}`,
        buildUpdatePayload(selectedClient)
      );

      setClients((prev) =>
        prev.map((client) => (client._id === selectedClient._id ? response.data : client))
      );

      alert('Corporate client updated successfully.');
      handleCloseModal();
    } catch (error) {
      console.error('Error updating corporate client:', error);
      alert('Error updating corporate client.');
    }
  };

  if (!isAuthenticated()) {
    return <Navigate to="/" />;
  }

  const isReadOnly = modalMode === 'view';

  return (
    <div className="client-list-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <div className={`client-list-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="client-list-sticky-header">
          <Header />
        </div>
        <div className="client-list-scroll-area">
        <section className="client-list-section">
          <div className="client-page-hero corporate-client-hero">
            <div>
              <span className="client-page-eyebrow">System Settings / Client Management</span>
              <h1>Corporate Clients</h1>
              <p>Manage, view, and maintain all corporate client records from one place.</p>
            </div>
            <div className="client-hero-stats">
              <span>Total Clients</span>
              <strong>{summary.total}</strong>
              <small>{summary.active} active · {summary.addedThisMonth} recently added</small>
            </div>
          </div>

          <div className="client-summary-grid">
            {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
              <article className={`client-summary-card ${tone}`} key={label}>
                <Icon size={22} />
                <span>{label}</span>
                <strong>{value}</strong>
                <small>{note}</small>
              </article>
            ))}
          </div>

          <section className="client-filter-card">
            <div className="client-filter-header">
              <div>
                <h2>Search & Filters</h2>
                <p>Search by Client ID, company, contact person, phone, email, GST, or PAN.</p>
              </div>
              <div className="client-result-pill">{filteredClients.length} records</div>
            </div>
            <div className="client-filter-grid">
              <label className="client-search-field">
                <span>Search</span>
                <div>
                  <Search size={17} />
                  <input
                    type="search"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Client ID, company, contact, phone, email"
                  />
                </div>
              </label>
              <label>
                <span>Owner</span>
                <select value={filters.owner} onChange={(e) => handleFilterChange('owner', e.target.value)}>
                  <option value="">All owners</option>
                  {filterOptions.owners.map((owner) => <option key={owner} value={owner}>{owner}</option>)}
                </select>
              </label>
              <label>
                <span>Assigned User</span>
                <select
                  value={filters.assignedUser}
                  onChange={(e) => handleFilterChange('assignedUser', e.target.value)}
                >
                  <option value="">All users</option>
                  {filterOptions.assignedUsers.map((user) => <option key={user} value={user}>{user}</option>)}
                </select>
              </label>
              <label>
                <span>City</span>
                <select value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)}>
                  <option value="">All cities</option>
                  {filterOptions.cities.map((city) => <option key={city} value={city}>{city}</option>)}
                </select>
              </label>
              <label>
                <span>State</span>
                <select value={filters.state} onChange={(e) => handleFilterChange('state', e.target.value)}>
                  <option value="">All states</option>
                  {filterOptions.states.map((state) => <option key={state} value={state}>{state}</option>)}
                </select>
              </label>
              <label>
                <span>Status</span>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                  <option value="">All status</option>
                  {filterOptions.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
            </div>
            <div className="client-action-row">
              <button type="button" className="client-btn client-btn-primary" onClick={handleSearch}>
                <Search size={16} /> Search
              </button>
              <button type="button" className="client-btn client-btn-ghost" onClick={handleResetFilters}>
                <RefreshCw size={16} /> Reset
              </button>
              {canExportCorporateClients && (
                <button type="button" className="client-btn client-btn-success" onClick={() => exportClients()}>
                  <Download size={16} /> Export Excel
                </button>
              )}
              {canAddCorporateClients && (
                <button type="button" className="client-btn client-btn-dark" onClick={() => navigate('/corporate/clients-form')}>
                  <Plus size={16} /> Add Client
                </button>
              )}
            </div>
          </section>

          {selectedClientIds.length > 0 && (
            <div className="client-bulk-bar">
              <strong>{selectedClientIds.length} selected</strong>
              <div>
                {canReassignCorporateOwner && (
                  <button type="button" onClick={handleBulkAssignOwner}><UserCheck size={15} /> Bulk Assign Owner</button>
                )}
                {canExportCorporateClients && <button type="button" onClick={() => exportClients(selectedClients, 'selected-corporate-clients.xlsx')}><Download size={15} /> Bulk Export</button>}
                {canDeleteCorporateClients && <button type="button" className="is-danger" onClick={handleBulkDelete}><Trash2 size={15} /> Bulk Delete</button>}
              </div>
            </div>
          )}

          {loading ? (
            <div className="client-list-empty">Loading corporate clients...</div>
          ) : filteredClients.length === 0 ? (
            <div className="client-empty-state">
              <Building2 size={44} />
              <h3>No Corporate Clients Found</h3>
              <p>Try changing the filters or add a new corporate client record.</p>
              {canAddCorporateClients && (
                <button type="button" className="client-btn client-btn-primary" onClick={() => navigate('/corporate/clients-form')}>
                  <Plus size={16} /> Add Client
                </button>
              )}
            </div>
          ) : (
            <div className="client-table-card">
              <div className="client-table-toolbar">
                <div>
                  <h2>Corporate Client Records</h2>
                  <p>Sticky company column, quick actions, and compliance-friendly fields.</p>
                </div>
              </div>
              <div className="client-table-scroll">
                <table className="client-table corporate-client-table">
                  <thead>
                    <tr>
                      <th className="client-sticky-check">
                        <input
                          type="checkbox"
                          checked={selectedClientIds.length === filteredClients.length && filteredClients.length > 0}
                          onChange={(e) => toggleAllFilteredClients(e.target.checked)}
                          aria-label="Select all corporate clients"
                        />
                      </th>
                      <th className="client-sticky-name">Company</th>
                      <th>Owner</th>
                      <th>Contact Person</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>City</th>
                      <th>State</th>
                      <th>GST / PAN</th>
                      <th>Status</th>
                      {canShowCorporateActions && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => {
                      const isSelected = selectedClientIds.includes(client._id);
                      return (
                        <tr key={client._id} className={isSelected ? 'is-selected' : ''}>
                          <td className="client-sticky-check" data-label="Select">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleClientSelection(client._id)}
                              aria-label={`Select ${client.companyName || 'corporate client'}`}
                            />
                          </td>
                          <td className="client-sticky-name" data-label="Company">
                            <button
                              type="button"
                              className="client-name-cell"
                              onClick={canViewCorporateClients ? () => setQuickViewClient(client) : undefined}
                              disabled={!canViewCorporateClients}
                            >
                              <span className="client-avatar corporate-avatar">{getInitials(client.companyName)}</span>
                              <span>
                                <strong>{client.companyName || '-'}</strong>
                                <small>Client ID: {getClientReference(client)}</small>
                              </span>
                            </button>
                          </td>
                          <td data-label="Owner">
                            <div className="client-owner-cell">
                              <span>{getInitials(getOwnerLabel(client))}</span>
                              <strong>{getOwnerLabel(client)}</strong>
                            </div>
                          </td>
                          <td data-label="Contact Person">{client.contactPersonName || '-'}</td>
                          <td data-label="Phone">
                            {client.contactNo ? (
                              <a className="client-contact-link" href={`tel:${client.contactNo}`}>
                                <Phone size={15} /> {client.contactNo}
                              </a>
                            ) : '-'}
                          </td>
                          <td data-label="Email">
                            {client.email ? (
                              <a className="client-contact-link" href={`mailto:${client.email}`}>
                                <Mail size={15} /> {client.email}
                              </a>
                            ) : '-'}
                          </td>
                          <td data-label="City">{client.city || '-'}</td>
                          <td data-label="State">{client.state || '-'}</td>
                          <td data-label="GST / PAN">
                            <div className="corporate-compliance-cell">
                              <span>{client.gst || 'GST not added'}</span>
                              <small>{client.pan || 'PAN not added'}</small>
                            </div>
                          </td>
                          <td data-label="Status"><span className="client-status-badge">{getClientStatus(client)}</span></td>
                          {canShowCorporateActions && (
                            <td data-label="Actions">
                              <div className="client-action-group">
                                {canViewCorporateClients && (
                                  <button
                                    type="button"
                                    className="client-action-icon-btn client-action-icon-btn-view"
                                    onClick={() => setQuickViewClient(client)}
                                    aria-label={`View ${client.companyName || 'client'}`}
                                    title="View Client"
                                  >
                                    <Eye size={16} strokeWidth={2} />
                                  </button>
                                )}
                                {canEditCorporateClients && (
                                  <button
                                    type="button"
                                    className="client-action-icon-btn client-action-icon-btn-edit"
                                    onClick={() => openClientModal(client, 'edit')}
                                    aria-label={`Edit ${client.companyName || 'client'}`}
                                    title="Edit Client"
                                  >
                                    <Pencil size={16} strokeWidth={2} />
                                  </button>
                                )}
                                {canDeleteCorporateClients && (
                                  <button
                                    type="button"
                                    className="client-action-icon-btn client-action-icon-btn-delete"
                                    onClick={() => handleOpenDeleteConfirmationModal(client)}
                                    aria-label={`Delete ${client.companyName || 'client'}`}
                                    title="Delete Client"
                                  >
                                    <Trash2 size={16} strokeWidth={2} />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {quickViewClient && (
            <div className="client-drawer-overlay" onClick={() => setQuickViewClient(null)}>
              <aside className="client-quick-drawer" onClick={(e) => e.stopPropagation()}>
                <div className="client-drawer-head">
                  <div className="client-drawer-title">
                    <span className="client-avatar client-avatar-lg corporate-avatar">{getInitials(quickViewClient.companyName)}</span>
                    <div>
                      <h3>{quickViewClient.companyName || 'Corporate Client'}</h3>
                      <p>Client ID: {getClientReference(quickViewClient)}</p>
                    </div>
                  </div>
                  <button type="button" className="client-drawer-close" onClick={() => setQuickViewClient(null)} aria-label="Close quick view">
                    <X size={18} />
                  </button>
                </div>
                <div className="client-drawer-body">
                  <section>
                    <h4>Company Details</h4>
                    <div className="client-detail-grid">
                      <span>Status</span><strong>{getClientStatus(quickViewClient)}</strong>
                      <span>Customer Type</span><strong>{quickViewClient.customerType || 'Corporate'}</strong>
                      <span>Income</span><strong>{quickViewClient.income || '-'}</strong>
                    </div>
                  </section>
                  <section>
                    <h4>Contact Details</h4>
                    <div className="client-drawer-contact">
                      <p><Users size={16} /> {quickViewClient.contactPersonName || '-'}</p>
                      <a href={quickViewClient.contactNo ? `tel:${quickViewClient.contactNo}` : undefined}>
                        <Phone size={16} /> {quickViewClient.contactNo || '-'}
                      </a>
                      <a href={quickViewClient.email ? `mailto:${quickViewClient.email}` : undefined}>
                        <Mail size={16} /> {quickViewClient.email || '-'}
                      </a>
                      <p><MapPin size={16} /> {quickViewClient.address || '-'}</p>
                    </div>
                  </section>
                  <section>
                    <h4>Compliance</h4>
                    <div className="client-detail-grid">
                      <span>GST</span><strong>{quickViewClient.gst || '-'}</strong>
                      <span>PAN</span><strong>{quickViewClient.pan || '-'}</strong>
                    </div>
                  </section>
                  <section>
                    <h4>Assigned User</h4>
                    <div className="client-owner-cell client-owner-cell-large">
                      <span>{getInitials(getAssignedUserLabel(quickViewClient))}</span>
                      <strong>{getAssignedUserLabel(quickViewClient)}</strong>
                    </div>
                  </section>
                  <section>
                    <h4>Notes</h4>
                    <p className="client-drawer-muted">{quickViewClient.note || 'No notes available.'}</p>
                  </section>
                </div>
              </aside>
            </div>
          )}

          {showModal && selectedClient && (
            <div className="client-data-modal-overlay">
              <div className="client-edit-modal-content client-edit-modal">
                <button className="close-btn" onClick={handleCloseModal}>X</button>
                <h3>{isReadOnly ? 'View Corporate Client Details' : 'Edit Corporate Client Details'}</h3>
                <div className="client-edit-summary">
                  <span className="client-avatar client-avatar-lg corporate-avatar">{getInitials(selectedClient.companyName)}</span>
                  <div>
                    <strong>{selectedClient.companyName || 'Corporate Client'}</strong>
                    <small>Client ID: {getClientReference(selectedClient)}</small>
                  </div>
                  <div>
                    <span>{getClientStatus(selectedClient)}</span>
                    <small>{getOwnerLabel(selectedClient) !== '-' ? `Owner: ${getOwnerLabel(selectedClient)}` : 'Owner not assigned'}</small>
                  </div>
                </div>
                <form className="client-edit-form">
                  <div className="client-edit-section-title">
                    <h4>Company Details</h4>
                    <p>Core company identity and ownership information.</p>
                  </div>

                  <label>Client ID:</label>
                  <input type="text" value={selectedClient.autoGeneratedId || ''} readOnly />

                  <label>Customer Type:</label>
                  <input type="text" value={selectedClient.customerType || 'Corporate'} readOnly />

                  <label>Company Name:</label>
                  <input
                    type="text"
                    value={selectedClient.companyName || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, companyName: e.target.value })}
                  />

                  {canReassignCorporateOwner && (
                    <>
                      <label>Owner:</label>
                      <select
                        value={getOwnerValue(selectedClient.ownerId)}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          setSelectedClient({ ...selectedClient, ownerId: e.target.value })
                        }
                      >
                        <option value="">Select owner</option>
                        {employeeOptions.map((employee) => (
                          <option key={employee._id} value={employee._id}>
                            {employee.name}
                            {employee.employeeId ? ` (${employee.employeeId})` : ''}
                          </option>
                        ))}
                      </select>

                      <div className="owner-history-panel client-edit-full-width">
                        <h4>Reassignment Log</h4>
                        {selectedClient.ownerHistory?.length ? (
                          <div className="owner-history-list">
                            {selectedClient.ownerHistory
                              .slice()
                              .reverse()
                              .map((entry, index) => (
                                <div className="owner-history-item" key={`${entry.changedAt}-${index}`}>
                                  <strong>{entry.fromOwnerName}</strong> to <strong>{entry.toOwnerName}</strong>
                                  <span>
                                    {entry.changedByName} on {formatAuditTime(entry.changedAt)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="owner-history-empty">No reassignment log available.</p>
                        )}
                      </div>
                    </>
                  )}

                  <div className="client-edit-section-title">
                    <h4>Contact & Location</h4>
                    <p>Primary contact channels and office address details.</p>
                  </div>

                  <label>Contact Person Name:</label>
                  <input
                    type="text"
                    value={selectedClient.contactPersonName || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, contactPersonName: e.target.value })}
                  />

                  <label>Contact Number:</label>
                  <input
                    type="text"
                    value={selectedClient.contactNo || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, contactNo: e.target.value })}
                  />

                  <label>Email:</label>
                  <input
                    type="email"
                    value={selectedClient.email || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, email: e.target.value })}
                  />

                  <label>PIN Code:</label>
                  <input
                    type="text"
                    value={selectedClient.pinCode || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, pinCode: e.target.value })}
                  />

                  <label>State:</label>
                  <input
                    type="text"
                    value={selectedClient.state || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, state: e.target.value })}
                  />

                  <label>City:</label>
                  <input
                    type="text"
                    value={selectedClient.city || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, city: e.target.value })}
                  />

                  <label>Address:</label>
                  <textarea
                    rows="3"
                    value={selectedClient.address || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, address: e.target.value })}
                  />

                  <div className="client-edit-section-title">
                    <h4>Compliance & Financials</h4>
                    <p>Business identifiers and financial profile.</p>
                  </div>

                  <label>Income:</label>
                  <input
                    type="text"
                    value={selectedClient.income || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, income: e.target.value })}
                  />

                  <label>GST Number:</label>
                  <input
                    type="text"
                    value={selectedClient.gst || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, gst: e.target.value })}
                  />

                  <label>PAN Number:</label>
                  <input
                    type="text"
                    value={selectedClient.pan || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, pan: e.target.value })}
                  />

                  <div className="client-edit-section-title">
                    <h4>Notes</h4>
                    <p>Internal servicing notes for this corporate client.</p>
                  </div>

                  <label>Customer Note:</label>
                  <textarea
                    rows="4"
                    value={selectedClient.note || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, note: e.target.value })}
                  />

                  <div className="client-data-modal-actions">
                    {!isReadOnly && (
                      <button type="button" onClick={handleUpdate}>Update</button>
                    )}
                    <button type="button" onClick={handleCloseModal}>
                      {isReadOnly ? 'Close' : 'Cancel'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showDeleteConfirmationModal && selectedClient && (
            <div className="client-data-modal-overlay">
              <div className="client-edit-modal-content client-document-modal">
                <button className="close-btn" onClick={handleCloseDeleteConfirmationModal}>X</button>
                <h3>Delete Corporate Client</h3>
                <div className="corporate-delete-confirm">
                  <p>
                    Type the company name <strong>{selectedClient.companyName || '-'}</strong> to confirm deletion.
                  </p>
                  <input
                    type="text"
                    value={confirmationCompany}
                    onChange={(e) => setConfirmationCompany(e.target.value)}
                    placeholder="Enter company name"
                  />
                  <div className="client-data-modal-actions">
                    <button className="confirm-btn" type="button" onClick={handleDelete}>Confirm Deletion</button>
                    <button className="cancel-btn" type="button" onClick={handleCloseDeleteConfirmationModal}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
        </div>
      </div>
    </div>
  );
};

export default CorporateClientData;
